import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/AWS.css";
import awsLogo from "../../assets/images/partners/aws.png";

/* ── Animated Counter ── */
function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}

function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Data ── */
const whyCards = [
  { icon: "☁️", title: "Global Cloud Infrastructure", desc: "AWS operates 31 geographic regions with 99 Availability Zones — delivering the world's most extensive global cloud network for latency-sensitive, compliance-driven, and mission-critical workloads." },
  { icon: "🔒", title: "Security & Compliance Leadership", desc: "AWS maintains 143 security standards and compliance certifications including ISO 27001, SOC 2, PCI DSS, HIPAA, FedRAMP, and GDPR — the broadest compliance portfolio in cloud computing." },
  { icon: "⚡", title: "Broadest Service Portfolio", desc: "240+ cloud services spanning compute, storage, databases, AI/ML, analytics, IoT, and developer tools — enabling organizations to build, run, and scale virtually any application on AWS." },
  { icon: "💰", title: "Economic Efficiency", desc: "Pay-as-you-go pricing, Reserved Instance savings, Savings Plans, and Spot Instances help organizations reduce infrastructure costs by 20–70% versus on-premises alternatives." },
  { icon: "🤖", title: "AI & Machine Learning Native", desc: "Amazon SageMaker, Bedrock, and 50+ AI/ML services make AWS the cloud of choice for organizations deploying generative AI, predictive analytics, and intelligent automation at scale." },
  { icon: "🏗️", title: "ALG Data Guard Expertise", desc: "ALG Data Guard's AWS-trained engineers design secure landing zones, execute cloud migrations, implement governance frameworks, and operate your AWS environment on an ongoing basis." },
];

const awsServices = [
  {
    name: "Amazon EC2 & Compute",
    tag: "Compute",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=700&q=80",
    desc: "Scalable virtual computing across 750+ instance types. From burstable t3 instances to GPU-accelerated p4d and AI-optimized Inferentia/Trainium instances for any workload profile.",
    pills: ["EC2 Auto Scaling", "ECS/EKS Containers", "Lambda Serverless", "Graviton ARM"],
  },
  {
    name: "Storage & Data Lakes",
    tag: "Storage",
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=700&q=80",
    desc: "Purpose-built storage services for every use case — from scalable S3 object storage and EFS file systems to high-performance EBS volumes and Glacier archive storage with sub-millisecond retrieval.",
    pills: ["Amazon S3", "EBS & EFS", "S3 Glacier", "AWS Backup"],
  },
  {
    name: "Networking & Connectivity",
    tag: "Networking",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=700&q=80",
    desc: "Build isolated, secure, and scalable networks with Amazon VPC, Direct Connect for dedicated hybrid connectivity, CloudFront CDN, and Route 53 DNS — with sub-millisecond latency globally.",
    pills: ["Amazon VPC", "Direct Connect", "CloudFront CDN", "Route 53 DNS"],
  },
  {
    name: "Identity & Security",
    tag: "Security",
    img: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=700&q=80",
    desc: "Defense-in-depth security across every layer — IAM fine-grained access control, AWS Shield DDoS protection, WAF application firewall, GuardDuty threat detection, and Security Hub aggregation.",
    pills: ["AWS IAM", "AWS Shield", "GuardDuty", "Security Hub"],
  },
  {
    name: "Databases & Analytics",
    tag: "Databases",
    img: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=700&q=80",
    desc: "Purpose-built managed database services for relational, NoSQL, in-memory, graph, time-series, and ledger workloads — eliminating database administration overhead and delivering enterprise reliability.",
    pills: ["Amazon RDS", "DynamoDB", "Redshift", "ElastiCache"],
  },
  {
    name: "AI & Machine Learning",
    tag: "Artificial Intelligence",
    img: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=700&q=80",
    desc: "Amazon SageMaker end-to-end ML platform, Amazon Bedrock for foundation models, and 50+ purpose-built AI services for vision, NLP, speech, forecasting, and code generation.",
    pills: ["SageMaker", "Amazon Bedrock", "Amazon Q", "Rekognition"],
  },
];

const industries = [
  { icon: "🏥", name: "Healthcare & Life Sciences", desc: "HIPAA-eligible services, Amazon HealthLake FHIR data lake, and genomics computing for precision medicine and clinical data modernization." },
  { icon: "🏦", name: "Financial Services", desc: "PCI-DSS compliant infrastructure, low-latency compute for trading systems, and regulatory data retention on purpose-built compliant services." },
  { icon: "🎓", name: "Education", desc: "Scalable learning management systems, research computing, and student data governance across higher education and K-12 institutions." },
  { icon: "🏛️", name: "Government", desc: "FedRAMP High, GovCloud(US), and IL-2/IL-4/IL-5 environments for federal, state, and local government workloads." },
  { icon: "🛒", name: "Retail & eCommerce", desc: "High-availability storefronts, real-time inventory management, personalization engines, and fraud detection for modern retail operations." },
  { icon: "🏭", name: "Manufacturing", desc: "Industrial IoT, predictive maintenance with ML, connected factory analytics, and supply chain optimization on AWS IoT and SageMaker." },
  { icon: "🎮", name: "Media & Entertainment", desc: "Global content delivery, video transcoding, and interactive game infrastructure delivered by CloudFront, Elemental, and GameLift." },
  { icon: "🌐", name: "Technology & SaaS", desc: "Multi-tenant SaaS platform hosting, microservices on EKS, and global scale with 99.999% availability SLAs for technology companies." },
];

const migrationSteps = [
  { icon: "🔍", title: "Assess", desc: "Cloud readiness evaluation, dependency mapping, and total cost of ownership modeling." },
  { icon: "📐", title: "Architect", desc: "Landing zone design, account structure, VPC topology, and security framework." },
  { icon: "🔧", title: "Pilot", desc: "Proof-of-concept workload migration to validate architecture and refine approach." },
  { icon: "🚀", title: "Migrate", desc: "Structured migration using AWS Migration Hub, DMS, and MGN for servers." },
  { icon: "🔒", title: "Secure", desc: "Implement governance, monitoring, and compliance controls across the new environment." },
  { icon: "⚙️", title: "Optimize", desc: "Continuous cost, performance, and reliability optimization using AWS Well-Architected." },
];

const benefits = [
  { icon: "💰", title: "40–70% Infrastructure Cost Savings", desc: "Eliminate data center capital expenditure and shift to elastic, pay-per-use cloud consumption aligned to actual business demand." },
  { icon: "⚡", title: "Deploy in Minutes, Not Months", desc: "AWS's 240+ managed services eliminate provisioning delays — reducing time-to-market from months to days for new application launches." },
  { icon: "🛡️", title: "Enterprise-Grade Security by Default", desc: "AWS's shared responsibility model with 143+ compliance certifications gives organizations enterprise security without the security engineering overhead." },
  { icon: "🌐", title: "Global Scale in One Click", desc: "Expand into any of AWS's 31 global regions and 99 Availability Zones on demand — serving customers globally without geographic infrastructure investment." },
];

const certifications = [
  { badge: "🏆", name: "AWS Partner Network (APN)", desc: "Registered AWS Partner with validated cloud practice and certified technical staff" },
  { badge: "⭐", name: "AWS Well-Architected Review", desc: "Authorized to conduct AWS Well-Architected Framework Reviews for enterprise clients" },
  { badge: "☁️", name: "AWS Cloud Practitioner", desc: "AWS certified engineers across cloud fundamentals and architecture" },
  { badge: "🔐", name: "AWS Security Specialty", desc: "Certified AWS security specialists for identity, compliance, and threat detection" },
];

const faqData = [
  { q: "What is the AWS Well-Architected Framework and why does it matter?", a: "The AWS Well-Architected Framework is AWS's best-practice guidance for building cloud workloads across six pillars: Operational Excellence, Security, Reliability, Performance Efficiency, Cost Optimization, and Sustainability. ALG Data Guard uses the WA Framework to review existing AWS environments and identify architectural risks, security gaps, and cost optimization opportunities. A Well-Architected Review is one of the highest-value engagements we offer at no additional cost for eligible clients." },
  { q: "What is an AWS Landing Zone and do I need one?", a: "An AWS Landing Zone is a pre-configured, secure multi-account AWS environment based on AWS best practices. It establishes account structure, centralized logging, security guardrails, network topology, and identity federation before any workloads are deployed. ALG Data Guard recommends building a proper Landing Zone before migration — it significantly reduces security risk and operational complexity as your AWS environment grows beyond the initial workloads." },
  { q: "How does AWS pricing work and how can I optimize costs?", a: "AWS uses a pay-per-use model with several pricing mechanisms: On-Demand (pay by the hour/second), Reserved Instances (1 or 3-year commitments for up to 72% savings), Savings Plans (flexible commitment-based discounts), and Spot Instances (spare capacity at 60–90% discount for interruptible workloads). ALG Data Guard analyzes your AWS usage, identifies over-provisioned resources, recommends Reserved Instance or Savings Plan purchases, and implements automated cost governance — typically reducing AWS spend by 20–40% in mature environments." },
  { q: "What is the difference between AWS regions and Availability Zones?", a: "AWS Regions are geographic areas (e.g., us-east-1 in Northern Virginia) containing multiple physically separate data centers called Availability Zones (AZs). Each AZ has independent power, cooling, and networking. Deploying across multiple AZs within a region provides high availability — if one AZ fails, your application continues in others. Multi-region architectures add disaster recovery capability and global latency optimization. ALG Data Guard architects multi-AZ deployments by default for all production workloads." },
  { q: "How does ALG Data Guard support AWS migration projects?", a: "ALG Data Guard follows a structured 6-phase AWS migration methodology: Assess (cloud readiness and TCO analysis), Architect (Landing Zone and workload design), Pilot (proof-of-concept migration), Migrate (structured workload migration using AWS MGN, DMS, and DataSync), Secure (governance and compliance implementation), and Optimize (ongoing cost and performance tuning). We handle the full migration lifecycle — from discovery through post-migration optimization." },
  { q: "What AWS managed services does ALG Data Guard operate for clients?", a: "ALG Data Guard's AWS managed services include: infrastructure monitoring and alerting, EC2 patch management, backup configuration and verification, cost optimization reviews, security event response, compliance reporting, and capacity planning. We offer both co-managed (augmenting your internal team) and fully managed (ALG Data Guard as primary operator) models, with 24/7 critical incident response and monthly service reviews." },
];

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);
const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export default function AWS() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);

  const c1 = useCounter(400, 2200, metricsInView);
  const c2 = useCounter(99, 2000, metricsInView);
  const c3 = useCounter(240, 2000, metricsInView);
  const c4 = useCounter(31, 2000, metricsInView);

  return (
    <div className="aws-v2-page pd-page">
      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={awsLogo} alt="Amazon Web Services" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Cloud Platform & Infrastructure
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Amazon Web Services</h1>
            <p className="pd-identity-desc">
              Amazon Web Services is the world's most comprehensive and widely adopted cloud platform,
              offering 240+ fully featured services from data centers globally. ALG Data Guard designs,
              migrates, secures, and operates AWS environments for enterprise organizations —
              helping them accelerate cloud adoption with confidence, governance, and measurable ROI.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="aws-hero">
        <div className="aws-hero__bg-grid" aria-hidden="true" />
        <div className="aws-hero__glow1" aria-hidden="true" />
        <div className="aws-hero__glow2" aria-hidden="true" />
        <div className="aws-hero__inner">
          <div className="aws-hero__content">
            <div className="aws-hero__kicker">
              <span className="aws-hero__kicker-dot" />
              AWS Partner Network — Cloud Solutions Partner
            </div>
            <h2 className="aws-hero__title">
              Build <em>Secure, Scalable</em> Cloud Infrastructure on AWS
            </h2>
            <p className="aws-hero__body">
              From secure landing zone design and cloud migration to AI-ready infrastructure and
              managed cloud operations — ALG Data Guard guides organizations through every stage
              of AWS adoption with architecture expertise, security controls, and continuous optimization
              that delivers measurable business value.
            </p>
            <div className="aws-hero__actions">
              <button className="aws-btn-primary" onClick={() => navigate("/contact")}>
                Start Your AWS Journey <ArrowIcon />
              </button>
              <button className="aws-btn-outline" onClick={() => navigate("/contact")}>
                Talk to a Cloud Expert
              </button>
            </div>
            <div className="aws-hero__stats">
              <div>
                <div className="aws-hero__stat-value">#1</div>
                <div className="aws-hero__stat-label">Global Cloud Platform</div>
              </div>
              <div>
                <div className="aws-hero__stat-value">31</div>
                <div className="aws-hero__stat-label">Global Regions</div>
              </div>
              <div>
                <div className="aws-hero__stat-value">240+</div>
                <div className="aws-hero__stat-label">Cloud Services</div>
              </div>
            </div>
          </div>

          {/* Cloud Architecture Diagram */}
          <div className="aws-hero__arch">
            <div className="aws-hero__arch-title">AWS Reference Architecture — Secure Landing Zone</div>
            <div className="aws-arch-layer aws-arch-layer--region">
              <span>🌍</span>
              <div>
                <div style={{ fontWeight: 700, color: "#fff", marginBottom: 4 }}>AWS Regions & Availability Zones</div>
                <div className="aws-arch-pills">
                  <span className="aws-arch-pill">us-east-1</span>
                  <span className="aws-arch-pill">eu-west-1</span>
                  <span className="aws-arch-pill">ap-southeast-1</span>
                </div>
              </div>
            </div>
            <div className="aws-arch-connector">↕</div>
            <div className="aws-arch-layer aws-arch-layer--services">
              <span>⚙️</span>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Core Cloud Services</div>
                <div className="aws-arch-pills">
                  <span className="aws-arch-pill">EC2</span>
                  <span className="aws-arch-pill">S3</span>
                  <span className="aws-arch-pill">RDS</span>
                  <span className="aws-arch-pill">Lambda</span>
                  <span className="aws-arch-pill">EKS</span>
                </div>
              </div>
            </div>
            <div className="aws-arch-connector">↕</div>
            <div className="aws-arch-layer aws-arch-layer--security">
              <span>🔐</span>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Security & Compliance Layer</div>
                <div className="aws-arch-pills">
                  <span className="aws-arch-pill aws-arch-pill--green">IAM</span>
                  <span className="aws-arch-pill aws-arch-pill--green">GuardDuty</span>
                  <span className="aws-arch-pill aws-arch-pill--green">Config</span>
                  <span className="aws-arch-pill aws-arch-pill--green">CloudTrail</span>
                </div>
              </div>
            </div>
            <div style={{ marginTop: "1.2rem", textAlign: "center", fontSize: "0.8rem", color: "#ffb833", fontWeight: 600 }}>
              🛡️ Managed by ALG Data Guard — AWS Partner Network
            </div>
          </div>
        </div>
      </section>

      {/* ── Why AWS ── */}
      <section className="aws-why">
        <div className="aws-shell">
          <div className="aws-why__head">
            <span className="aws-badge"><span className="aws-dot" /> Why Amazon Web Services</span>
            <h2 className="aws-section-title">The World's Leading Cloud Platform</h2>
            <p className="aws-section-sub">
              AWS has led the cloud infrastructure market for 18 consecutive years. Here's why leading enterprises
              choose AWS — and why ALG Data Guard is their trusted partner to design and operate it.
            </p>
          </div>
          <div className="aws-why__grid">
            {whyCards.map((card) => (
              <div key={card.title} className="aws-why-card">
                <div className="aws-why-card__icon">{card.icon}</div>
                <h3 className="aws-why-card__title">{card.title}</h3>
                <p className="aws-why-card__body">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Cloud Services ── */}
      <section className="aws-services">
        <div className="aws-shell">
          <div className="aws-services__head">
            <span className="aws-badge"><span className="aws-dot" /> AWS Service Portfolio</span>
            <h2 className="aws-section-title">Core AWS Services ALG Data Guard Delivers</h2>
            <p className="aws-section-sub">
              Our AWS-certified engineers design, deploy, and operate solutions across the full
              spectrum of AWS services — tailored to your workload requirements, security posture, and business goals.
            </p>
          </div>
          <div className="aws-services__grid">
            {awsServices.map((svc) => (
              <div key={svc.name} className="aws-service-card">
                <div className="aws-service-card__img-wrap">
                  <img src={svc.img} alt={svc.name} className="aws-service-card__img" />
                  <span className="aws-service-card__tag">{svc.tag}</span>
                </div>
                <div className="aws-service-card__body">
                  <h3 className="aws-service-card__name">{svc.name}</h3>
                  <p className="aws-service-card__desc">{svc.desc}</p>
                  <div className="aws-service-card__pills">
                    {svc.pills.map((p) => (
                      <span key={p} className="aws-service-card__pill">{p}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industry Solutions ── */}
      <section className="aws-industries">
        <div className="aws-shell">
          <div className="aws-industries__head">
            <span className="aws-badge" style={{ background: "rgba(255,153,0,0.2)", borderColor: "rgba(255,153,0,0.4)", color: "#ffcc66" }}>
              <span className="aws-dot" /> Industries We Serve
            </span>
            <h2 className="aws-section-title">AWS Solutions Across Every Industry</h2>
            <p className="aws-section-sub">
              AWS's deep portfolio of industry-specific services and compliance certifications
              makes it the cloud of choice for regulated, performance-sensitive, and mission-critical environments.
            </p>
          </div>
          <div className="aws-industries__grid">
            {industries.map((ind) => (
              <div key={ind.name} className="aws-industry-card">
                <span className="aws-industry-card__icon">{ind.icon}</span>
                <h3 className="aws-industry-card__name">{ind.name}</h3>
                <p className="aws-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Architecture Section ── */}
      <section className="aws-arch-section">
        <div className="aws-shell">
          <div className="aws-arch-section__inner">
            <div>
              <img
                src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80"
                alt="Cloud data center infrastructure"
                className="aws-arch-section__img"
              />
            </div>
            <div>
              <span className="aws-badge"><span className="aws-dot" /> AWS Foundation</span>
              <h2 className="aws-arch-section__title">Secure Landing Zones Designed for Enterprise Scale</h2>
              <p className="aws-arch-section__body">
                ALG Data Guard builds AWS environments with strong governance from day one — using AWS
                Control Tower, AWS Organizations, and the Well-Architected Framework to establish account
                structure, centralized security monitoring, compliant networking, and identity federation
                before any workloads are deployed.
              </p>
              <p className="aws-arch-section__body">
                Whether you're migrating your first workload or optimizing a mature multi-account environment,
                our Landing Zone approach ensures you build on a foundation that scales with confidence.
              </p>
              <div className="aws-arch-caps">
                {["Control Tower", "AWS Organizations", "Service Control Policies", "Centralized Logging", "Security Hub", "IAM Identity Center", "Config Rules", "CloudTrail"].map((cap) => (
                  <span key={cap} className="aws-arch-cap">{cap}</span>
                ))}
              </div>
              <button className="aws-btn-primary" onClick={() => navigate("/contact")}>
                Design My Landing Zone <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Migration Journey ── */}
      <section className="aws-journey">
        <div className="aws-shell">
          <div className="aws-journey__head">
            <span className="aws-badge"><span className="aws-dot" /> Migration Methodology</span>
            <h2 className="aws-section-title">Our Proven AWS Migration Path</h2>
            <p className="aws-section-sub">
              A structured, risk-managed approach to AWS adoption — from initial discovery
              through to optimized production operations.
            </p>
          </div>
          <div className="aws-journey__steps">
            {migrationSteps.map((step, i) => (
              <div key={i} className="aws-journey__step">
                <div className="aws-journey__num">{step.icon}</div>
                <h3 className="aws-journey__step-title">{step.title}</h3>
                <p className="aws-journey__step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Business Benefits ── */}
      <section className="aws-benefits">
        <div className="aws-shell">
          <div className="aws-benefits__inner">
            <div className="aws-benefits__img-wrap">
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80"
                alt="Cloud operations team"
                className="aws-benefits__img"
              />
              <div className="aws-benefits__img-overlay" />
            </div>
            <div className="aws-benefits__content">
              <span className="aws-badge" style={{ background: "rgba(255,153,0,0.2)", borderColor: "rgba(255,153,0,0.4)", color: "#ffcc66" }}>
                <span className="aws-dot" /> Business Value
              </span>
              <h2 className="aws-section-title">Measurable Returns from Cloud Transformation</h2>
              <p className="aws-section-sub">
                AWS cloud adoption delivers quantifiable business outcomes — from capital expenditure
                elimination to developer velocity improvements and global scale enablement.
              </p>
              <div className="aws-benefits__list">
                {benefits.map((b) => (
                  <div key={b.title} className="aws-benefit-item">
                    <div className="aws-benefit-item__icon">{b.icon}</div>
                    <div>
                      <div className="aws-benefit-item__title">{b.title}</div>
                      <p className="aws-benefit-item__desc">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="aws-metrics" ref={metricsRef}>
        <div className="aws-shell">
          <div className="aws-metrics__inner">
            <div>
              <div className="aws-metric__value">{c1}+</div>
              <div className="aws-metric__label">AWS Projects Delivered</div>
            </div>
            <div>
              <div className="aws-metric__value">{c2}.9%</div>
              <div className="aws-metric__label">Cloud Uptime SLA</div>
            </div>
            <div>
              <div className="aws-metric__value">{c4}</div>
              <div className="aws-metric__label">AWS Global Regions</div>
            </div>
            <div>
              <div className="aws-metric__value">{c3}+</div>
              <div className="aws-metric__label">AWS Services Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="aws-certs">
        <div className="aws-shell">
          <div className="aws-certs__head">
            <span className="aws-badge"><span className="aws-dot" /> AWS Partner Credentials</span>
            <h2 className="aws-section-title">Validated AWS Partner Expertise</h2>
            <p className="aws-section-sub">
              ALG Data Guard's AWS certifications demonstrate our commitment to delivering
              best-practice cloud solutions for enterprise organizations.
            </p>
          </div>
          <div className="aws-certs__grid">
            {certifications.map((c) => (
              <div key={c.name} className="aws-cert-card">
                <div className="aws-cert-card__badge">{c.badge}</div>
                <div className="aws-cert-card__name">{c.name}</div>
                <div className="aws-cert-card__desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="aws-faq">
        <div className="aws-shell">
          <div className="aws-faq__head">
            <span className="aws-badge" style={{ background: "rgba(255,153,0,0.2)", borderColor: "rgba(255,153,0,0.4)", color: "#ffcc66" }}>
              <span className="aws-dot" /> Frequently Asked Questions
            </span>
            <h2 className="aws-section-title">AWS Cloud Questions, Answered</h2>
            <p className="aws-section-sub">
              Everything you need to know about AWS and how ALG Data Guard helps organizations adopt, migrate, and optimize on the cloud.
            </p>
          </div>
          <div className="aws-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`aws-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="aws-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="aws-faq-item__q">{faq.q}</span>
                  <span className="aws-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="aws-faq-item__answer">
                  <div className="aws-faq-item__answer-inner">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="aws-cta">
        <div className="aws-cta__inner">
          <span className="aws-badge" style={{ background: "rgba(255,153,0,0.2)", borderColor: "rgba(255,153,0,0.4)", color: "#ffcc66", margin: "0 auto 1.2rem" }}>
            <span className="aws-dot" /> Ready for Cloud?
          </span>
          <h2 className="aws-cta__title">
            Move Forward with Confidence on Amazon Web Services
          </h2>
          <p className="aws-cta__sub">
            Whether you're planning your first cloud migration, optimizing mature AWS workloads, or
            building AI-ready infrastructure — ALG Data Guard's AWS-certified engineers are ready to
            design, implement, and operate the right AWS strategy for your organization.
          </p>
          <div className="aws-cta__actions">
            <button className="aws-btn-primary" onClick={() => navigate("/contact")}>
              Start Your AWS Journey <ArrowIcon />
            </button>
            <button className="aws-btn-outline" onClick={() => navigate("/contact")}>
              Get a Cloud Assessment
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}